<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, string } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading
} from 'front-library'
import {
  convertArrayToString,
  getPayroll,
  getReportRequest,
  getProcessCountry,
  getConcepts,
  getListYear
} from '@/app/core/services/reports'
import {
  getSucursals,
  ReportRAPHN,
  generateRAPHN
} from '@/app/core/services/reports/banks'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsProcesses = ref<string[]>([])
const itemsConcepts = ref<any[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsYears = ref<any[]>([])
const itemsSucursals = ref<any[]>([])
const itemsTypeFile = ref<any[]>([
  { id: 'normalPayroll', name: t('reports.columns.normalForm') },
  { id: 'supplementaryPayroll', name: t('reports.columns.complementaryForm') }
])
const searchTypeFile = ref<string>('')
const itemsMonths = ref([
  { id: 1, name: t('reports.months.january') },
  { id: 2, name: t('reports.months.february') },
  { id: 3, name: t('reports.months.march') },
  { id: 4, name: t('reports.months.april') },
  { id: 5, name: t('reports.months.may') },
  { id: 6, name: t('reports.months.june') },
  { id: 7, name: t('reports.months.july') },
  { id: 8, name: t('reports.months.august') },
  { id: 9, name: t('reports.months.september') },
  { id: 10, name: t('reports.months.october') },
  { id: 11, name: t('reports.months.november') },
  { id: 12, name: t('reports.months.december') }
])
const itemsIdentificationFormat = ref([
  { id: 'employee_identification_number', name: 'Cedula' },
  { id: 'employee_fiscal_tax', name: 'Fiscal ID' }
])
const searchMonths = ref<string>('')
const searchConcept = ref<string>('')
const searchIdentificationFormat = ref<string>('')
const urlFile = ref(null)
const refresh = ref()
const path = route.params.code as string
const decodeCode = atob(path)

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  concepts: string().required(t('form.valid.required')),
  month: string().required(t('form.valid.required')),
  year: string().required(t('form.valid.required')),
  subsidiaries: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  fileType: string().required(t('form.valid.required')),
  identificationFormat: string().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: processes } = useField<string[]>(ref('processes'))
const { value: concepts } = useField<string>(ref('concepts'))
const { value: month } = useField<string>('month')
const { value: year } = useField<string>('year')
const { value: subsidiaries } = useField<string[]>('subsidiaries')
const { value: fileType } = useField<string>('fileType')
const { value: format } = useField(ref('format'))
const { value: identificationFormat } = useField<string>(
  ref('identificationFormat')
)

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.concepts = allIdsConceptsSelected.value
  const createReport: ReportRAPHN = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateRAPHN(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = []
  try {
    if (payrolls.value?.length > 0 && processes.value !== undefined) {
      const strProcesses = await convertArrayToString(processes.value)
      const strPayrolls = await convertArrayToString(payrolls.value)
      allConcepts.value = await getConcepts(
        strPayrolls,
        'rap',
        '',
        strProcesses
      )
      if (concepts.value !== undefined) filterCodeConcepts()
      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]
      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = async () => {
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => item.code === concepts.value)
    .map(_ => _.id)
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseProcess, promiseSucursals] =
      await Promise.all([
        getPayroll(store.user?.id as string),
        getProcessCountry(),
        getSucursals(storePayroll?.payroll?.company?.id as string)
      ])
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = promiseProcess
    processes.value = itemsProcesses.value
      .filter((_: any) => _.id === 'N' || _.id === 'E')
      .map((_: any) => _.id)
    itemsSucursals.value = promiseSucursals
    subsidiaries.value = itemsSucursals.value.map((_: any) => _.id)
    identificationFormat.value = 'employee_identification_number'
    fileType.value = 'normalPayroll'
    itemsYears.value = getListYear()
    listConcepts()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.rap')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-6 gap-4">
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DMultiselect
                :label="t('reports.fields.payrolls')"
                v-model="payrolls"
                :headerTitle="t('reports.fields.payrolls')"
                :required="true"
                :options="itemsPayrolls"
                :error="errors.payrolls"
                @change="listConcepts()" />
            </div>
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DMultiselect
                :label="t('reports.fields.processes')"
                v-model="processes"
                :header-title="t('reports.fields.processes')"
                :required="true"
                :options="itemsProcesses"
                :error="errors.processes"
                @change="listConcepts()" />
            </div>
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DSelectFieldSearch
                :label="t('reports.fields.concept')"
                v-model="concepts"
                :value-key="'code'"
                :options="filterOptions(itemsConcepts, searchConcept)"
                :required="true"
                :error="errors.concepts"
                @change="filterCodeConcepts()"
                @filter="searchConcept = $event" />
            </div>
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DSelectFieldSearch
                :label="t('reports.fields.month')"
                v-model="month"
                :required="true"
                :options="filterOptions(itemsMonths, searchMonths)"
                :error="errors.month"
                @filter="searchMonths = $event" />
            </div>
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DSelectFieldSearch
                :label="t('reports.fields.year')"
                v-model="year"
                :required="true"
                :options="itemsYears"
                :error="errors.year" />
            </div>
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DMultiselect
                :label="t('reports.fields.branches')"
                v-model="subsidiaries"
                :header-title="t('reports.fields.branches')"
                :required="true"
                :options="itemsSucursals"
                :error="errors.sucursals" />
            </div>
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DSelectFieldSearch
                :label="t('reports.fields.typeFile')"
                v-model="fileType"
                :required="true"
                :options="filterOptions(itemsTypeFile, searchTypeFile)"
                @filter="searchTypeFile = $event"
                :error="errors.typeFile">
              </DSelectFieldSearch>
            </div>
            <hr class="col-span-6" />
            <h1
              class="col-span-6 text-gray-600"
              v-html="t('reports.tabs.modifiers')" />
            <div class="col-span-6 sm:col-span-3 lg:col-span-2">
              <DSelectFieldSearch
                :label="t('reports.fields.identificationFormat')"
                v-model="identificationFormat"
                :required="true"
                :options="
                  filterOptions(
                    itemsIdentificationFormat,
                    searchIdentificationFormat
                  )
                "
                :error="errors.identificationFormat"
                @filter="searchIdentificationFormat = $event" />
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (format = 'xlsx')"
                v-html="t('form.button.excel')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (format = 'txt')"
                v-html="t('form.button.file')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
